/**
 * src/core/telemetry — injected telemetry seam (PRI-439 R14, design §22/§26-R14).
 *
 * src/core/ is OSS-eligible: it must not import PostHog, GA4, Sentry, or any
 * first-party analytics module. Instead, core code calls the two narrow
 * interfaces below; the HOST APP injects real implementations at the root
 * (src/App.tsx wires src/primate/analytics + @sentry/react). The default is
 * a no-op — the published package emits nothing unless the consumer opts in.
 *
 *   - TelemetryTracker.track(event, props): product analytics events. Event
 *     names are the snake_case names the host analytics module already uses
 *     (e.g. 'video_uploaded'), so the injected impl is a thin dispatch.
 *   - TelemetryErrorReporter: error/diagnostic channel (Sentry-shaped, but
 *     deliberately minimal — breadcrumbs, messages, exceptions).
 *
 * React context with a module-level fallback: core hooks are sometimes used
 * outside the provider in tests, so useTelemetry() never throws.
 */
import { createContext, useContext, type ReactNode } from 'react'

// ── Interfaces ────────────────────────────────────────────────────────────────

export interface TelemetryTracker {
  /** Product analytics event (snake_case name + flat props). */
  track(event: string, props?: Record<string, unknown>): void
  /** JS error surfaced to product analytics (host may also alert on it). */
  trackError(error: Error, context?: Record<string, unknown>): void
}

export type TelemetrySeverity = 'info' | 'warning' | 'error'

export interface TelemetryErrorReporter {
  addBreadcrumb(crumb: { category: string; message: string; level?: TelemetrySeverity; data?: Record<string, unknown> }): void
  captureMessage(message: string, opts?: { level?: TelemetrySeverity; tags?: Record<string, string>; extra?: Record<string, unknown> }): void
  captureException(error: unknown, opts?: { tags?: Record<string, string>; extra?: Record<string, unknown> }): void
}

export interface Telemetry {
  tracker: TelemetryTracker
  reporter: TelemetryErrorReporter
}

// ── No-op defaults (package consumers get silence unless they inject) ────────

export const noopTracker: TelemetryTracker = {
  track: () => {},
  trackError: () => {},
}

export const noopReporter: TelemetryErrorReporter = {
  addBreadcrumb: () => {},
  captureMessage: () => {},
  captureException: () => {},
}

export const noopTelemetry: Telemetry = { tracker: noopTracker, reporter: noopReporter }

// ── Context + module-level fallback ───────────────────────────────────────────

const TelemetryContext = createContext<Telemetry>(noopTelemetry)

/**
 * Module-level registry so non-React call sites (and hooks rendered outside
 * the provider in tests) still resolve the host telemetry once set. The
 * provider sets this on mount; setTelemetry() is the non-React escape hatch.
 */
let _current: Telemetry = noopTelemetry

export function setTelemetry(t: Telemetry): void {
  _current = t
}

export function getTelemetry(): Telemetry {
  return _current
}

export function TelemetryProvider({ telemetry, children }: { telemetry: Telemetry; children: ReactNode }) {
  // Keep the module fallback in sync for non-React call sites.
  _current = telemetry
  return <TelemetryContext.Provider value={telemetry}>{children}</TelemetryContext.Provider>
}

export function useTelemetry(): Telemetry {
  return useContext(TelemetryContext)
}
